import React, { useCallback, useState } from 'react';
import { Chip, Loader, TableSortable } from '@saviynt/design-system';

import type { RoleIntelligenceRow } from '../../../../mock-data/roleIntelligenceRolesTableMockData';
import ALL_MOCK_DATA from '../../../../mock-data/roleIntelligenceRolesTableMockData';
import { useInfiniteScroll } from '../../FUEInventory/FUEInventoryTable/hooks/useInfiniteScroll';

import './RoleIntelligenceTable.css';

const PAGE_SIZE = 10;

interface RoleIntelligenceTableProps {
  onRowClick?: (role: RoleIntelligenceRow) => void;
}

const SORT_OPTIONS = [
  {
    label: 'Ascending',
    value: 'asc',
    isDisabled: false,
    isChecked: false,
    icon: 'ArrowDown',
    shouldShowCheckIcon: true,
  },
  {
    label: 'Descending',
    value: 'desc',
    isDisabled: false,
    isChecked: false,
    icon: 'ArrowUp',
    shouldShowCheckIcon: true,
  },
];

// Column widths mirror the FUE Inventory table pattern:
// the pinned first column (Role Name) is wider, every other data column shares
// one width, and the trailing gear/action column is a fixed narrow width.
const PINNED_COLUMN_WIDTH = '20rem';
const COLUMN_WIDTH = '12.25rem';
const ACTION_COLUMN_WIDTH = '3rem';

const ACTION_COLUMN = {
  key: 'actionColumn',
  label: 'actionColumn',
  value: 'actionColumn',
  isSortable: false,
  isChecked: true,
  isPinned: true,
  isDisabled: true,
  isColumnConfigEnabled: true,
  width: ACTION_COLUMN_WIDTH,
};

const COLUMNS = [
  // Priority columns — visible by default, locked (Role Name) or toggleable
  {
    key: 'roleName',
    label: 'ROLE NAME',
    value: 'roleName',
    isSortable: true,
    isChecked: true,
    isPinned: true,
    isDisabled: true,
    isColumnConfigEnabled: true,
    width: PINNED_COLUMN_WIDTH,
  },
  {
    key: 'currentTier',
    label: 'CURRENT TIER',
    value: 'currentTier',
    isSortable: true,
    isChecked: true,
    isPinned: false,
    isDisabled: false,
    isColumnConfigEnabled: true,
    width: COLUMN_WIDTH,
  },
  {
    key: 'potentialTier',
    label: 'POTENTIAL TIER',
    value: 'potentialTier',
    isSortable: true,
    isChecked: true,
    isPinned: false,
    isDisabled: false,
    isColumnConfigEnabled: true,
    width: COLUMN_WIDTH,
  },
  {
    key: 'status',
    label: 'STATUS',
    value: 'status',
    isSortable: true,
    isChecked: true,
    isPinned: false,
    isDisabled: false,
    isColumnConfigEnabled: true,
    width: COLUMN_WIDTH,
  },
  {
    key: 'type',
    label: 'TYPE',
    value: 'type',
    isSortable: true,
    isChecked: true,
    isPinned: false,
    isDisabled: false,
    isColumnConfigEnabled: true,
    width: COLUMN_WIDTH,
  },
  {
    key: 'assignedUsers',
    label: 'ASSIGNED USERS',
    value: 'assignedUsers',
    isSortable: true,
    isChecked: true,
    isPinned: false,
    isDisabled: false,
    isColumnConfigEnabled: true,
    width: COLUMN_WIDTH,
  },
  // Optional columns — hidden by default, available via gear icon
  {
    key: 'totalCost',
    label: 'TOTAL COST',
    value: 'totalCost',
    isSortable: true,
    isChecked: false,
    isPinned: false,
    isDisabled: false,
    isColumnConfigEnabled: true,
    width: COLUMN_WIDTH,
  },
  {
    key: 'totalSavings',
    label: 'TOTAL SAVINGS',
    value: 'totalSavings',
    isSortable: true,
    isChecked: false,
    isPinned: false,
    isDisabled: false,
    isColumnConfigEnabled: true,
    width: COLUMN_WIDTH,
  },
  ACTION_COLUMN,
];

/**
 * Maps a tier name to the CSS class that applies its exact hex color.
 * Kept in sync with the FUE Inventory table/panel so tier chips share the same
 * colors across all of License Intelligence.
 */
function getTierChipClass(tier: string): string {
  if (!tier) {
    return 'LicenseManagement-tierChip--unknown';
  }

  if (tier.startsWith('Advanced')) {
    return 'LicenseManagement-tierChip--advanced';
  }

  if (tier.startsWith('Core')) {
    return 'LicenseManagement-tierChip--core';
  }

  if (tier.startsWith('Self-Service')) {
    return 'LicenseManagement-tierChip--selfService';
  }

  if (tier.startsWith('Developer')) {
    return 'LicenseManagement-tierChip--developer';
  }

  return 'LicenseManagement-tierChip--unknown';
}

// Status chip colors mirror the FUE Inventory table so status types are in sync
// across License Intelligence: Over-licensed (critical), Dormant (warning),
// Compliant (success).
function getStatusChipColor(
  status: string
): 'success' | 'critical' | 'warning' {
  if (status === 'Over-licensed') {
    return 'critical';
  }

  if (status === 'Dormant') {
    return 'warning';
  }

  return 'success';
}

function mapRow(row: RoleIntelligenceRow) {
  return {
    id: row.id,
    roleName: row.roleName,
    type: row.type,
    assignedUsers: String(row.users),
    currentTier: (
      <Chip
        kind='subtleFilled'
        label={row.currentTier}
        size='small'
        className={getTierChipClass(row.currentTier)}
      />
    ),
    totalCost: `$${(row.costPerUser * row.users).toLocaleString()}`,
    potentialTier: (
      <Chip
        kind='subtleFilled'
        label={row.potentialTier}
        size='small'
        className={getTierChipClass(row.potentialTier)}
      />
    ),
    totalSavings:
      row.totalSavings > 0 ? `$${row.totalSavings.toLocaleString()}` : '—',
    status: (
      <Chip
        kind='subtleFilled'
        label={row.status}
        color={getStatusChipColor(row.status)}
        size='small'
      />
    ),
  };
}

/**
 * @description Renders the Role Intelligence data table. Displays paginated role
 * rows with tier/status chips, loads additional rows via infinite scroll, keeps
 * the Role Name column pinned/sticky, and supports column sorting.
 * @param {RoleIntelligenceTableProps} props - Component props.
 * @param {(role: RoleIntelligenceRow) => void} [props.onRowClick] - Optional
 * callback invoked with the matched role when a table row is clicked.
 * @returns {React.ReactElement} The rendered Role Intelligence table.
 */
function RoleIntelligenceTable({
  onRowClick = undefined,
}: RoleIntelligenceTableProps): React.ReactElement {
  const [columns, setColumns] = useState(COLUMNS);
  const [allRows, setAllRows] = useState<RoleIntelligenceRow[]>(
    ALL_MOCK_DATA.slice(0, PAGE_SIZE)
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(ALL_MOCK_DATA.length > PAGE_SIZE);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const loadNextPage = useCallback(() => {
    if (!hasMore || isFetchingMore) {
      return;
    }

    setIsFetchingMore(true);
    const nextPage = currentPage + 1;

    window.setTimeout(() => {
      const start = nextPage * PAGE_SIZE;
      const end = Math.min(start + PAGE_SIZE, ALL_MOCK_DATA.length);

      setAllRows((prev) => [...prev, ...ALL_MOCK_DATA.slice(start, end)]);
      setCurrentPage(nextPage);
      setHasMore(end < ALL_MOCK_DATA.length);
      setIsFetchingMore(false);
    }, 600);
  }, [currentPage, hasMore, isFetchingMore]);

  const sentinelRef = useInfiniteScroll({
    hasNextPage: hasMore,
    isFetchingNextPage: isFetchingMore,
    fetchNextPage: loadNextPage,
  });

  const sortConfigs = columns
    .filter((col) => col.isSortable)
    .map((col) => ({ key: col.key, sortOptions: SORT_OPTIONS }));

  const rows = allRows.map(mapRow);

  const handleRowClick = (rowData: { id: string }) => {
    const role = ALL_MOCK_DATA.find((r) => r.id === rowData.id);

    if (role && onRowClick) {
      onRowClick(role);
    }
  };

  return (
    <div className='LicenseManagement-RoleIntelligenceTable'>
      <TableSortable
        columns={columns}
        setColumns={setColumns}
        data={rows}
        sortConfigs={sortConfigs}
        isVirtualizationEnabled={false}
        columnConfigs={{
          isDndDisabled: false,
          isHeaderSticky: true,
          isHorizontalScroll: true,
        }}
        onRowClick={handleRowClick}
      />

      {isFetchingMore && (
        <div className='LicenseManagement-RoleIntelligenceTable-loadingMore'>
          <Loader kind='dots' size='medium' format='inline' color='brand' />
        </div>
      )}

      <div ref={sentinelRef} style={{ height: '1px' }} aria-hidden='true' />
    </div>
  );
}

RoleIntelligenceTable.defaultProps = {
  onRowClick: undefined,
};

export default RoleIntelligenceTable;
